export type PlayerTicketType = "support" | "rule_question" | "player_report" | "ban_appeal" | "staff_report" | "other";

export interface PlayerTicketFormConfig {
  guidance: string;
  subjectLabel: string;
  subjectPlaceholder: string;
  bodyLabel: string;
  bodyPlaceholder: string;
  bodyHelp: string;
  bodyMinLength: number;
  showTarget: boolean;
  targetRequired: boolean;
  showEvidence: boolean;
  confirmation: string;
}

const ticketForms: Record<PlayerTicketType, PlayerTicketFormConfig> = {
  support: {
    guidance: "Tell staff what is not working and what you need help with.",
    subjectLabel: "What do you need help with?",
    subjectPlaceholder: "Example: Garage retrieval failed",
    bodyLabel: "Describe the problem",
    bodyPlaceholder: "Explain what you expected, what happened instead, and anything you already tried.",
    bodyHelp: "Include an approximate time if the issue involved the game server.",
    bodyMinLength: 10,
    showTarget: false,
    targetRequired: false,
    showEvidence: true,
    confirmation: "Staff will receive this private support request. Check the details and any selected evidence before submitting."
  },
  rule_question: {
    guidance: "Ask for a rule clarification without filling in incident-report fields.",
    subjectLabel: "Rule or topic",
    subjectPlaceholder: "Example: Group limits",
    bodyLabel: "What would you like clarified?",
    bodyPlaceholder: "Ask your question and include the situation that prompted it, if useful.",
    bodyHelp: "Evidence is not needed for a rule question. You can add more context after submitting.",
    bodyMinLength: 3,
    showTarget: false,
    targetRequired: false,
    showEvidence: false,
    confirmation: "Staff will receive this private rule question. Check that your question is clear before submitting."
  },
  player_report: {
    guidance: "Identify the player if you can, then provide enough detail for staff to investigate fairly.",
    subjectLabel: "Brief summary",
    subjectPlaceholder: "Example: Combat logging near Delta",
    bodyLabel: "What happened?",
    bodyPlaceholder: "Include the approximate date, time, location, rule involved, and a clear sequence of events.",
    bodyHelp: "State only what you observed. Staff can review relevant server records.",
    bodyMinLength: 10,
    showTarget: true,
    targetRequired: false,
    showEvidence: true,
    confirmation: "Staff will receive this private player report. Check the incident details and any selected evidence before submitting."
  },
  ban_appeal: {
    guidance: "Explain the punishment you are appealing and why it should be reviewed.",
    subjectLabel: "Punishment being appealed",
    subjectPlaceholder: "Example: Seven-day combat logging ban",
    bodyLabel: "Why should this be reviewed?",
    bodyPlaceholder: "Explain what happened, why you believe the decision should be reviewed, and any relevant context.",
    bodyHelp: "Be honest and specific. Staff can see your existing moderation history.",
    bodyMinLength: 10,
    showTarget: false,
    targetRequired: false,
    showEvidence: true,
    confirmation: "Staff will receive this private appeal. Check your explanation and any selected evidence before submitting."
  },
  staff_report: {
    guidance: "Raise a confidential concern for authorised owners to review.",
    subjectLabel: "Brief summary",
    subjectPlaceholder: "Short, neutral summary of the concern",
    bodyLabel: "Describe your concern",
    bodyPlaceholder: "Identify the staff member if needed, then explain what happened with approximate dates and times.",
    bodyHelp: "Only authorised owners can access staff reports. The reported staff member is not notified.",
    bodyMinLength: 10,
    showTarget: true,
    targetRequired: false,
    showEvidence: true,
    confirmation: "Authorised owners will receive this confidential staff report. Check the details and selected evidence before submitting."
  },
  other: {
    guidance: "Use this when none of the other categories fit your request.",
    subjectLabel: "What is your request about?",
    subjectPlaceholder: "Short summary of your request",
    bodyLabel: "How can we help?",
    bodyPlaceholder: "Explain what you need from the team and include any useful context.",
    bodyHelp: "Staff may ask follow-up questions in the ticket.",
    bodyMinLength: 3,
    showTarget: false,
    targetRequired: false,
    showEvidence: false,
    confirmation: "Staff will receive this private request. Check that it clearly explains what you need before submitting."
  }
};

export function playerTicketFormConfig(type: string | undefined): PlayerTicketFormConfig {
  return ticketForms[type as PlayerTicketType] ?? ticketForms.support;
}
